// Live metal price ticker that replaces the legacy header search bar.
// Hydrates from the canonical hub and streams gold/silver/platinum/copper
// in GBP without browser-side provider polling.
//
// Defensive: while loading, after errors, or when the backend is in
// "demo" mode (no live data) we still render a sensible placeholder so
// the header layout never collapses.
import { TrendingUp, TrendingDown, Minus, Coins, Pause, Play } from "lucide-react";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { customFetch } from "@workspace/api-client-react";
import { cn } from "@/lib/utils";
import {
  applyPerQuoteQualityStatus,
  useLiveMarket,
  hubSnapshot,
  type HubSnapshot,
} from "@/hooks/use-live-market";

// Unit conversions. Shared market quotes are per troy ounce; convert them to
// grams for the secondary display. Legacy pound quotes still carry their unit.
const TROY_OZ_TO_G = 31.1034768;
const LB_TO_G = 453.59237;

function fmtGbp(value: number, fractionDigits: number): string {
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    maximumFractionDigits: fractionDigits,
    minimumFractionDigits: fractionDigits,
  }).format(value);
}

function pickFractionDigits(value: number): number {
  if (value < 0.1) return 4;
  if (value < 10) return 3;
  return 2;
}

function perGram(value: number, unit: "oz" | "lb"): number {
  return unit === "lb" ? value / LB_TO_G : value / TROY_OZ_TO_G;
}

export function marketFreshnessLabel({
  disconnected,
  isError,
  hasData,
  quoteQuality,
  streamQuality,
}: {
  disconnected: boolean;
  isError: boolean;
  hasData: boolean;
  quoteQuality?: string;
  streamQuality: string;
}): string {
  if (disconnected) return "DISCONNECTED";
  if (isError && !hasData) return "Unavailable";
  const quality = String(quoteQuality ?? streamQuality).toUpperCase();
  return quality === "DELAYED" ? "LAST UPDATED" : quality;
}

export function formatQuoteAge(timestamp: string | null | undefined, now = Date.now()): string {
  if (!timestamp) return "age unknown";
  const observedAt = Date.parse(timestamp);
  if (!Number.isFinite(observedAt)) return "age unknown";
  const seconds = Math.max(0, Math.floor((now - observedAt) / 1_000));
  if (seconds < 60) return seconds === 0 ? "just now" : `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

const HEADER_QUOTE_FRESHNESS_MS = 30_000;
const HEADER_QUOTE_MAX_AGE_MS = 120_000;

export function providerObservationIsFresh(
  timestamp: string | null | undefined,
  now = Date.now(),
): boolean {
  if (!timestamp) return false;
  const observedAt = Date.parse(timestamp);
  const age = now - observedAt;
  return Number.isFinite(observedAt) && age >= 0 && age <= HEADER_QUOTE_FRESHNESS_MS;
}

export function freshTickerSpotValue(
  value: number | null,
  quality: string | null | undefined,
  providerTimestamp: string | null | undefined,
  now = Date.now(),
): number | null {
  if (value == null || !Number.isFinite(value) || value <= 0
    || ["STALE", "UNAVAILABLE"].includes(String(quality ?? "").toUpperCase())
    || !providerTimestamp || !Number.isFinite(Date.parse(providerTimestamp))
    || now - Date.parse(providerTimestamp) < 0
    || now - Date.parse(providerTimestamp) > HEADER_QUOTE_MAX_AGE_MS) {
    return null;
  }
  return value;
}

type Quote = {
  metal: string;
  gbpPerOz: number | null;
  change: number | null;
  changePct: number | null;
  unit?: "oz" | "lb";
  quality?: string;
  sourceTimestamp?: string | null;
  retrievedAt?: string;
};

function MetalCell({
  label,
  color,
  quote,
  disconnected,
}: {
  label: string;
  color: string;
  quote: Quote | undefined;
  disconnected: boolean;
}) {
  if (!quote || quote.gbpPerOz == null) {
    const age = quote ? formatQuoteAge(quote.sourceTimestamp) : null;
    return (
      <div className="flex items-center gap-1.5 text-sm">
        <Coins className={cn("h-4 w-4", color)} />
        <span className="font-medium text-muted-foreground">{label}: Unavailable</span>
        {quote ? (
          <span
            className="text-muted-foreground text-xs tabular-nums"
            role="status"
            aria-label={`${label} quote unavailable, observed ${age}`}
          >
            {age}
          </span>
        ) : null}
      </div>
    );
  }
  const isUp = quote.change != null && quote.change > 0;
  const isDown = quote.change != null && quote.change < 0;
  const Arrow = isUp ? TrendingUp : isDown ? TrendingDown : Minus;
  const trendColor = isUp ? "text-emerald-600" : isDown ? "text-red-600" : "text-muted-foreground";
  const unit = quote.unit ?? "oz";
  const gram = perGram(quote.gbpPerOz, unit);
  const freshness = marketFreshnessLabel({
    disconnected,
    isError: false,
    hasData: true,
    quoteQuality: quote.quality,
    streamQuality: quote.quality ?? "UNAVAILABLE",
  });
  const age = formatQuoteAge(quote.sourceTimestamp);
  return (
    <div
      className="flex items-center gap-1.5 text-sm whitespace-nowrap"
      data-testid={`ticker-${label.toLowerCase()}`}
    >
      <Coins className={cn("h-4 w-4", color)} />
      <span className="font-medium">
        {label}: {fmtGbp(quote.gbpPerOz, pickFractionDigits(quote.gbpPerOz))}
        <span className="text-muted-foreground text-xs ml-0.5">/{unit}</span>
      </span>
      <span className="text-muted-foreground text-xs tabular-nums">
        · {fmtGbp(gram, pickFractionDigits(gram))}
        <span className="ml-0.5">/g</span>
      </span>
      <span className={cn("flex items-center gap-0.5 text-xs tabular-nums", trendColor)}>
        <Arrow className="h-3 w-3" />
        {quote.changePct == null ? "—" : `${quote.changePct > 0 ? "+" : ""}${quote.changePct.toFixed(2)}%`}
      </span>
      <span className="text-muted-foreground text-xs" aria-label={`${label} observed ${age}`}>
        {age}
      </span>
    </div>
  );
}

const mobileMetals = [
  { metal: "gold", label: "Gold", color: "text-amber-500" },
  { metal: "silver", label: "Silver", color: "text-zinc-400" },
  { metal: "platinum", label: "Platinum", color: "text-sky-300" },
  { metal: "palladium", label: "Palladium", color: "text-stone-400" },
  { metal: "copper", label: "Copper", color: "text-orange-600" },
] as const;

function MobileMetalCell({
  label,
  color,
  quote,
}: {
  label: string;
  color: string;
  quote: Quote | undefined;
}) {
  const age = formatQuoteAge(quote?.sourceTimestamp);
  const value = quote?.gbpPerOz;

  if (value == null) {
    return (
      <div
        className="flex shrink-0 items-center gap-1.5 whitespace-nowrap text-xs"
        data-testid={`mobile-ticker-${label.toLowerCase()}`}
      >
        <Coins className={cn("h-3.5 w-3.5 shrink-0", color)} aria-hidden />
        <span className="shrink-0 font-semibold">{label}</span>
        <span
          className="font-medium text-muted-foreground"
          role="status"
          aria-label={`${label} quote unavailable, observed ${age}`}
        >
          Unavailable
        </span>
        <span
          className="shrink-0 text-[10px] text-muted-foreground tabular-nums"
          aria-label={`${label} observed ${age}`}
          data-testid={`mobile-market-age-${label.toLowerCase()}`}
        >
          {age}
        </span>
      </div>
    );
  }
  return (
    <div
      className="flex shrink-0 items-center gap-1.5 whitespace-nowrap text-xs"
      data-testid={`mobile-ticker-${label.toLowerCase()}`}
    >
      <Coins className={cn("h-3.5 w-3.5 shrink-0", color)} aria-hidden />
      <span className="shrink-0 font-semibold">{label}</span>
      <span className="font-medium tabular-nums">
        {fmtGbp(value, pickFractionDigits(value))}/{quote?.unit ?? "oz"}
      </span>
      <span
        className="shrink-0 text-[10px] text-muted-foreground tabular-nums"
        aria-label={`${label} observed ${age}`}
        data-testid={`mobile-market-age-${label.toLowerCase()}`}
      >
        {age}
      </span>
    </div>
  );
}

export function MobileMetalTicker({
  enabledMetals,
  quotes,
  disconnected,
  isLoading,
}: {
  enabledMetals: ReadonlySet<string>;
  quotes: Record<string, Quote | undefined>;
  disconnected: boolean;
  isLoading: boolean;
}) {
  const [paused, setPaused] = useState(false);
  const visibleMetals = mobileMetals.filter(({ metal }) => enabledMetals.has(metal));
  const cells = visibleMetals.map(({ metal, label, color }) => (
    <MobileMetalCell key={metal} label={label} color={color} quote={quotes[metal]} />
  ));

  if (cells.length === 0) {
    return (
      <div className="flex min-w-0 items-center gap-2 text-xs" data-testid="mobile-market-quote-unavailable">
        <Coins className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
        <span className="truncate text-muted-foreground">
          {isLoading ? "Loading market quotes…" : "Market quotes unavailable"}
        </span>
      </div>
    );
  }

  return (
    <div className="mobile-market-ticker flex min-w-0 items-center gap-2" data-testid="mobile-market-ticker">
      <button
        type="button"
        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        aria-label={paused ? "Resume metal price ticker" : "Pause metal price ticker"}
        aria-pressed={paused}
        onClick={() => setPaused((current) => !current)}
        data-testid="mobile-market-ticker-toggle"
      >
        {paused ? <Play className="h-3.5 w-3.5" aria-hidden /> : <Pause className="h-3.5 w-3.5" aria-hidden />}
      </button>
      <div
        className="mobile-market-viewport min-w-0 flex-1 overflow-x-auto overscroll-x-contain"
        role="region"
        aria-label="Live metal prices"
        tabIndex={0}
        data-testid="mobile-market-scroll"
      >
        <div className={cn("mobile-market-track flex w-max items-center gap-4", paused && "is-paused")}>
          <div className="flex items-center gap-4 pr-4">{cells}</div>
          <div className="flex items-center gap-4 pr-4" aria-hidden="true">{cells}</div>
        </div>
      </div>
      {disconnected ? <span className="sr-only" role="status">Disconnected; showing last update</span> : null}
    </div>
  );
}

export function MetalPriceTicker() {
  const availability = useQuery({
    queryKey: ["/api/markets/availability"],
    queryFn: () => customFetch<{ enabledMetals: string[] }>("/api/markets/availability", { method: "GET", responseType: "json" }),
    staleTime: 60_000,
  });
  // Until the global operator state is confirmed, render no metal cells rather
  // than briefly exposing a cached quote for one that was just disabled.
  const enabledMetals = new Set(availability.data?.enabledMetals ?? []);
  const live = useLiveMarket<HubSnapshot>({
    snapshotUrl: "/api/markets/snapshot?metals=gold,silver,platinum,palladium,copper&currency=GBP",
    streamUrl: "/api/markets/stream?metals=gold,silver,platinum,palladium,copper&currency=GBP",
    coordinationKey: "header-market-hub-snapshot-v1",
    parseSnapshot: hubSnapshot,
    authenticated: true,
    applyEvent: (current, event) => {
      if (event.kind === "status") {
        return {
          ...current,
          quality: event.payload.quality,
          quotes: applyPerQuoteQualityStatus(current.quotes, event.payload),
        };
      }
      if (event.kind === "heartbeat") return current;
      return event.payload;
    },
  });
  const data = Object.fromEntries((live.data?.quotes ?? []).map((quote) => {
    const providerTimestamp = typeof quote.provenance?.metalTimestamp === "string"
      ? quote.provenance.metalTimestamp
      : quote.sourceTimestamp;
    const gbpPerOz = freshTickerSpotValue(
      quote.value,
      quote.quality,
      providerTimestamp,
    );
    return [quote.metal, {
      metal: quote.metal,
      gbpPerOz,
      change: gbpPerOz == null ? null : quote.change ?? null,
      changePct: gbpPerOz == null ? null : quote.changePct ?? null,
      unit: quote.canonicalUnit === "pound" ? "lb" as const : "oz" as const,
      quality: gbpPerOz == null ? "UNAVAILABLE" : quote.quality,
      sourceTimestamp: providerTimestamp,
      retrievedAt: quote.retrievedAt,
    }];
  }));

  // The cells, rendered as a single sequence. We render this group twice
  // back-to-back inside the marquee track so the -50% translate loops
  // seamlessly. `aria-hidden` on the second copy prevents screen readers
  // from reading prices twice.
  const cells = (
    <>
      {enabledMetals.has("gold") ? <MetalCell label="Gold" color="text-amber-500" quote={data?.gold} disconnected={live.disconnected} /> : null}
      {enabledMetals.has("silver") ? <MetalCell label="Silver" color="text-zinc-400" quote={data?.silver} disconnected={live.disconnected} /> : null}
      {enabledMetals.has("platinum") ? <MetalCell label="Platinum" color="text-sky-300" quote={data?.platinum} disconnected={live.disconnected} /> : null}
      {enabledMetals.has("palladium") ? <MetalCell label="Palladium" color="text-stone-400" quote={data?.palladium} disconnected={live.disconnected} /> : null}
      {enabledMetals.has("copper") ? <MetalCell label="Copper" color="text-orange-600" quote={data?.copper} disconnected={live.disconnected} /> : null}
    </>
  );

  return (
      <>
        <div className="min-w-0 md:hidden" aria-label="Market quotes">
          <MobileMetalTicker
            enabledMetals={enabledMetals}
            quotes={data}
            disconnected={live.disconnected}
            isLoading={live.isLoading}
          />
        </div>
        <div
        // Mask edges so prices fade in/out at the boundaries instead of
        // popping. The mask is identical on both sides; tweak the 5% stops
        // to widen / narrow the fade.
         className="market-ticker-shell group relative hidden min-w-0 flex-1 overflow-hidden md:block"
        style={{
          maskImage:
            "linear-gradient(to right, transparent 0, #000 5%, #000 95%, transparent 100%)",
          WebkitMaskImage:
            "linear-gradient(to right, transparent 0, #000 5%, #000 95%, transparent 100%)",
        }}
        aria-label="Live metal price ticker"
      >
        <div
          className={cn(
            "market-ticker-track flex w-max items-center gap-8 text-sm whitespace-nowrap",
          )}
        >
          <div className="flex items-center gap-8 pr-8">{cells}</div>
          <div className="flex items-center gap-8 pr-8" aria-hidden="true">
            {cells}
          </div>
        </div>
        {live.disconnected ? <span className="absolute right-1 top-1/2 -translate-y-1/2 rounded-full border border-amber-300 bg-background px-2 py-0.5 text-[10px] font-medium text-amber-700" role="status">Disconnected · showing last update</span> : null}
        {live.isLoading ? <span className="sr-only">Loading prices…</span> : null}
        </div>
      </>
  );
}
